import { createFileRoute, Outlet, Link } from '@tanstack/react-router';
import { Newspaper, Sparkles } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * `/tresci` — dynamic-content hub (CMS).
 *
 *   /tresci/aktualnosci — blog / news with rich-text body + cover photo
 *   /tresci/realizacje  — case studies with before/after image pairs +
 *                          patient consent gate
 *
 * Both share the same brief PUT plumbing (slices `posts` and `case_studies`).
 * The build workflow auto-emits /aktualnosci(/<slug>) + /realizacje(/<slug>)
 * for any tenant that has entries.
 */
export const Route = createFileRoute('/_app/tresci')({
  staticData: { crumb: 'Treści' },
  component: TresciLayout,
});

const TABS = [
  {
    to: '/tresci/aktualnosci',
    label: 'Aktualności',
    description: 'Blog / news z edytorem WYSIWYG',
    Icon: Newspaper,
  },
  {
    to: '/tresci/realizacje',
    label: 'Realizacje',
    description: 'Zdjęcia przed i po z zgodą pacjenta',
    Icon: Sparkles,
  },
] as const;

function TresciLayout() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Treści dynamiczne
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Twórz wpisy aktualności i realizacji. Każdy wpis ląduje jako podstrona
          publicznej witryny — <code>/aktualnosci/&lt;slug&gt;</code> lub <code>/realizacje/&lt;slug&gt;</code>.
        </p>
      </header>

      <nav aria-label="Sekcje treści" className="border-b border-border">
        <ul role="list" className="flex flex-wrap gap-1">
          {TABS.map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                className="group inline-flex items-center gap-2 rounded-t-md border-b-2 border-transparent px-3 py-2 text-sm font-medium text-text-muted hover:text-text data-[status=active]:border-accent data-[status=active]:text-accent"
                activeProps={{ 'data-status': 'active' } as any}
              >
                <t.Icon className="size-4" aria-hidden="true" />
                {t.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <Outlet />
    </div>
  );
}
